import { useCallback } from 'react'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { selectIsDarkMode, setIsDarkMode } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { DISPLAY_SETTINGS_ANALYTICS } from '../constants/constants'

/**
 * Single entry point for changing the theme (ENG-33713). Both the legacy
 * `DarkModeSetting` and the rebrand `DarkModeSection` go through this so the
 * toggle analytics fire identically on either path.
 *
 * Unlike `useIncidentAudio` there is no mutation to reconcile — the preference
 * is device-local, persisted by redux-persist under `persist:ui-v2` — so there
 * is nothing to roll back and no Sentry capture.
 */
export const useDarkModeToggle = () => {
  const dispatch = useAppDispatch()
  const isDarkMode = useAppSelector(selectIsDarkMode)

  const toggleDarkMode = useCallback(
    (nextIsDark?: boolean) => {
      const shouldTurnOn = nextIsDark ?? !isDarkMode

      dispatch(setIsDarkMode(shouldTurnOn))

      trackEvent(DISPLAY_SETTINGS_ANALYTICS.toggleDarkMode, {
        theme: shouldTurnOn ? 'dark' : 'light',
      })
    },
    [dispatch, isDarkMode]
  )

  return { isDarkMode, toggleDarkMode }
}
